import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  CheckCircle2, Circle, Sparkles, ArrowRight, X,
  Heart, Building2, Briefcase, BadgeCheck, Globe,
  PartyPopper, Rocket
} from 'lucide-react';
import api from '@/lib/api';

// Partner role icons
const PARTNER_ICONS = {
  clinical_support_partner: Heart,
  organization_partner: Building2,
  strategic_partner: Briefcase,
  affiliate_partner: BadgeCheck,
  marketplace_partner: Globe
};

// Partner role colors
const PARTNER_COLORS = {
  clinical_support_partner: 'from-pink-500 to-rose-600',
  organization_partner: 'from-green-500 to-emerald-600',
  strategic_partner: 'from-amber-500 to-orange-600',
  affiliate_partner: 'from-orange-500 to-red-500',
  marketplace_partner: 'from-sky-500 to-blue-600'
};

const PartnerOnboarding = ({ user, onClose }) => {
  const navigate = useNavigate();
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeChecklist, setActiveChecklist] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [roleInfo, setRoleInfo] = useState(null);

  useEffect(() => {
    loadChecklists();
  }, [user]);

  const loadChecklists = async () => {
    if (!user?.id) return;
    
    try {
      const res = await api.get(`/partner-onboarding/checklist/${user.id}`);
      const lists = res.data.checklists || [];
      setChecklists(lists);
      
      // Find the most recent incomplete checklist
      const incompleteList = lists.find(c => !c.completed && !c.tour_completed);
      if (incompleteList) {
        setActiveChecklist(incompleteList);
        // Load role info
        const infoRes = await api.get(`/partner-onboarding/role-info/${incompleteList.partner_role}`);
        setRoleInfo(infoRes.data);
        setShowWelcome(true);
      }
    } catch (error) {
      console.log('No onboarding checklists:', error);
    } finally {
      setLoading(false);
    }
  };

  const completeStep = async (stepIndex) => {
    if (!activeChecklist) return;
    
    try {
      await api.put(`/partner-onboarding/checklist/${user.id}/complete-step`, {
        partner_role: activeChecklist.partner_role,
        step_index: stepIndex
      });
      
      // Reload checklists
      await loadChecklists();
      toast.success('Step completed!');
    } catch (error) {
      toast.error('Failed to complete step');
    }
  };

  const completeTour = async () => {
    if (!activeChecklist) return;
    
    try {
      await api.put(`/partner-onboarding/checklist/${user.id}/complete-tour`, {
        partner_role: activeChecklist.partner_role
      });
      
      toast.success('Onboarding complete! Welcome aboard!');
      setShowWelcome(false);
      
      // Navigate to the partner's dashboard
      if (roleInfo?.dashboard_url) {
        navigate(roleInfo.dashboard_url);
      }
      
      if (onClose) onClose();
    } catch (error) {
      toast.error('Failed to complete tour');
    }
  };

  const calculateProgress = () => {
    if (!activeChecklist?.items) return 0;
    const completed = activeChecklist.items.filter(item => item.completed).length;
    return Math.round((completed / activeChecklist.items.length) * 100);
  };

  if (loading || !showWelcome || !activeChecklist || !roleInfo) {
    return null;
  }

  const Icon = PARTNER_ICONS[activeChecklist.partner_role] || Sparkles;
  const gradientColor = PARTNER_COLORS[activeChecklist.partner_role] || 'from-purple-500 to-indigo-600';
  const progress = calculateProgress();

  return (
    <Dialog open={showWelcome} onOpenChange={setShowWelcome}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className={`-mx-6 -mt-6 mb-4 p-8 bg-gradient-to-r ${gradientColor} text-white rounded-t-lg`}>
            <div className="flex items-center gap-4">
              <div className="p-4 bg-white/20 rounded-xl backdrop-blur">
                <Icon className="w-10 h-10" />
              </div>
              <div>
                <DialogTitle className="text-2xl text-white flex items-center gap-2">
                  <PartyPopper className="w-6 h-6" />
                  Welcome, {user?.name}!
                </DialogTitle>
                <DialogDescription className="text-white/90 mt-1">
                  You&apos;re now a {roleInfo.name}
                </DialogDescription>
              </div>
            </div>
          </div>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Features Section */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Rocket className="w-5 h-5 text-purple-600" />
              What You Can Do Now
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {roleInfo.features?.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span className="text-sm text-gray-700">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Checklist Section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">Your Next Steps</h3>
              <Badge variant="secondary">{progress}% complete</Badge>
            </div>
            <Progress value={progress} className="mb-4" />
            
            <div className="space-y-2">
              {activeChecklist.items?.map((item, idx) => (
                <div 
                  key={idx}
                  onClick={() => !item.completed && completeStep(idx)}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-all cursor-pointer ${
                    item.completed 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-white border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                  }`}
                >
                  {item.completed ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <Circle className="w-5 h-5 text-gray-400" />
                  )}
                  <span className={item.completed ? 'text-green-700 line-through' : 'text-gray-700'}>
                    {item.step}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={() => setShowWelcome(false)}>
            I&apos;ll do this later
          </Button>
          <Button 
            onClick={completeTour}
            className={`bg-gradient-to-r ${gradientColor} text-white`}
          >
            Go to Dashboard
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PartnerOnboarding;
